import { useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { NumberField, Select, Button, Field } from "../components/rumonai";
import { ServicePrice, accountAmount, type ModelService, type PlatformUsage, type SupplyNode } from "../hub/modelSupply";
import { platformPost, platformRequest } from "./api";
import { PlatformSettings } from "./PlatformSettings";
import { MEDIA_CHOICES, ModelServiceActions, ModelServiceForm } from "./ModelServiceForm";
import { SupplyNodes } from "./SupplyNodes";
import {
  CommandFeedback, PageHeading, PlatformError, PlatformLoading, usePlatformCommand, usePlatformRead
} from "./shared";

interface Call {
  id: string;
  account_id: string;
  agent_id: string;
  agent_name?: string | null;
  service_id: string;
  status: string;
  reserved_cny: number;
  charged_cny: number | null;
  created_at: number;
  /** What the Hub recorded for the call; a refused one keeps what the supplier said. */
  usage?: { supplier_message?: string; characters?: number; supplier_actual_cost?: number | null };
}
/** One kind of vendor API the Hub can forward to, as its table (`hub/model_supply.py`) describes it. */
export interface ProtocolRow {
  protocol: string;
  kind: string;
  kind_title: string;
  title: string;
  /** False for a service whose requests name no model, such as web search. */
  names_model: boolean;
  /** "tokens" per million tokens; "call" per successful call; "clip" per clip; "free" only zero. */
  pricing: string;
  /** Where the vendor's API starts when that is fixed; the Hub appends the operation path. */
  address_hint: string;
  /** True when a service of this protocol may be made by a supply node instead of at a supplier's address. */
  nodes: boolean;
  /** The start every model id of this protocol has (the Agent's tools go by it); "" when any id will do. */
  model_prefix?: string;
}
interface SupplyData {
  protocols: ProtocolRow[];
  nodes?: SupplyNode[];
  model_vendors?: { value: string; title: string }[];
  services: ModelService[];
  accounts: (PlatformUsage & { id: string; name: string; role?: string; agent_count?: number })[];
  calls: Call[];
}
const MEDIA_KIND_OF = { supports_vision: "image", supports_audio: "audio", supports_video: "video" } as const;

/** What a chat service is set to read, each kind with what the test made of it: "能读取：文字、图片（测试通过）". */
function takes(service: ModelService): string {
  const kinds = MEDIA_CHOICES.filter(([field]) => service[field]).map(([field, title]) => {
    const tried = service.verification?.media?.[MEDIA_KIND_OF[field]]?.status;
    return title + (tried === "passed" ? "（测试通过）" : tried === "failed" ? "（测试未通过）" : tried === "unknown" ? "（结果未确认）" : "（还没测试）");
  });
  return `能读取：${["文字", ...kinds].join("、")}`;
}

export function ModelSupplySettings({ view = "services", accountId }: { view?: "services" | "credits" | "calls"; accountId?: string }) {
  const reading = usePlatformRead<SupplyData>(`/api/admin/models${accountId ? `?account=${encodeURIComponent(accountId)}` : ""}`);
  const servicesHeading = useRef<HTMLHeadingElement>(null);
  const serviceForm = useRef<HTMLFormElement>(null);
  const editOrigin = useRef<HTMLButtonElement | null>(null);
  const cancelledFrom = useRef<HTMLButtonElement | null>(null);
  const [editing, setEditing] = useState<{ service: ModelService | "new" } | null>(null);
  const editService = (service: ModelService | "new", origin: HTMLButtonElement) => {
    editOrigin.current = origin;
    // Each explicit click locates the editor; its service key still owns the draft.
    setEditing({ service });
  };
  useLayoutEffect(() => {
    if (editing) {
      serviceForm.current?.querySelector<HTMLInputElement>('input[name="title"]')?.focus({ preventScroll: true });
      serviceForm.current?.scrollIntoView({ block: "start" });
    } else if (cancelledFrom.current) {
      // An add button becomes enabled only after the editor has closed.
      const origin = cancelledFrom.current;
      cancelledFrom.current = null;
      (origin.isConnected ? origin : servicesHeading.current)?.focus();
    }
  }, [editing]);
  const [account, setAccount] = useState("");
  const [allowance, setAllowance] = useState("");
  const [reason, setReason] = useState("内测体验额度");
  const [operation, setOperation] = useState("add");
  const adjustmentId = useRef(crypto.randomUUID());
  const [review, setReview] = useState<Call | null>(null);
  const [actual, setActual] = useState("");
  const [evidence, setEvidence] = useState("");
  const command = usePlatformCommand();
  const accountName = (id: string) => reading.data?.accounts.find(row => row.id === id)?.name;
  const serviceTitle = (id: string) => reading.data?.services.find(row => row.id === id)?.title;
  const nodes = reading.data?.nodes ?? [];
  const saveAllowance = (event: FormEvent) => {
    event.preventDefault();
    if (!account || allowance === "" || !reason.trim()) return;
    void command.run(async () => {
      await platformRequest(`/api/admin/model-allowances/${encodeURIComponent(account)}/adjust`, {
        method: "POST", body: JSON.stringify({ amount_cny: Number(allowance), operation, request_id: adjustmentId.current, reason: reason.trim() }),
      });
      command.setNotice(operation === "add" ? "额度已增加。" : "额度已收回。");
      setAccount(""); setReason(""); reading.reload();
    });
  };
  const reconcile = (event: FormEvent) => {
    event.preventDefault();
    if (!review || actual === "" || !evidence.trim()) return;
    void command.run(async () => {
      await platformPost(`/api/admin/model-calls/${review.id}/reconcile`, {
        charged_cny: Number(actual),
        reason: evidence.trim()
      });
      command.setNotice("已按核对依据结算这次调用。");
      setReview(null);
      setActual("");
      setEvidence("");
      reading.reload();
    });
  };
  return <>
    {!accountId ? <PageHeading
      title={view === "services" ? "平台服务" : view === "credits" ? "用户额度" : "调用记录"}
      actions={
        <Button variant="secondary" onClick={reading.reload} disabled={reading.loading}>
          <Icon name="refresh" />刷新
        </Button>
      }
    >
      <p>{view === "services" ? "统一提供模型、配音、素材和搜索。用户无需填写供应商密钥。" : view === "credits" ? "发放体验额度，查看用户消耗。" : "按平台售价扣减的额度；不代表实际收款或供应商成本。"}</p>
    </PageHeading> : <div className="platform-explore-tools"><h2>{view === "credits" ? "用户额度" : "调用记录"}</h2><Button variant="secondary" onClick={reading.reload} disabled={reading.loading}><Icon name="refresh" />刷新</Button></div>}
    <CommandFeedback error={command.error} notice={command.notice} />
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!reading.data && reading.loading ? <PlatformLoading label="正在读取模型与额度" /> : null}
    {reading.data && view === "services" ? <section className="platform-supply-section">
      <div className="platform-explore-tools">
        <h2 ref={servicesHeading} tabIndex={-1}>提供的服务</h2>
        <Button variant="primary"

          onClick={event => editService("new", event.currentTarget)}
          disabled={Boolean(editing) || !reading.data}
        >
          <Icon name="plus" />添加服务
        </Button>
      </div>
      <p className="platform-meta">价格用于扣减用户额度。</p>
      {reading.data?.services.length === 0 ? <p>尚未添加服务。先配置一个对话模型，即可供 Agent 使用。</p> : null}
      <div className="platform-instance-list">
        {reading.data?.services.map(service => {
          const spec = reading.data?.protocols.find(row => row.protocol === service.protocol);
          return <article key={service.id}>
            <div className="platform-instance-summary">
              <div>
                <h3>{service.title}</h3>
                <p>{[spec?.kind_title, service.model || spec?.title || service.protocol,
                  service.node ? `由节点「${nodes.find(row => row.id === service.node)?.title ?? service.node}」提供` : "",
                  service.enabled ? "已启用" : "未启用",
                  service.is_default && service.enabled ? spec?.kind === "chat" ? "新 Agent 默认" : "默认服务" : ""].filter(Boolean).join(" · ")}</p>
                <p className="platform-meta">{service.verification ? `${service.verification.status === "passed" ? "测试通过" : service.verification.status === "failed" ? "测试失败" : "测试结果未确认"} · ${new Date(service.verification.at * 1000).toLocaleString("zh-CN")}` : "尚未测试"}</p>
                {spec?.kind === "chat" ? <p className="platform-meta">{takes(service)}</p> : null}
                <small><ServicePrice rate={{ ...service, pricing: spec?.pricing ?? "call" }} /></small>
                {service.verification && service.verification.status !== "passed" ? <p className="platform-inline-warning">{service.verification.message}</p> : null}
                {service.problem ? <p className="platform-inline-warning">{service.problem}</p> : null}
              </div>
              <Button variant="secondary" onClick={event => editService(service, event.currentTarget)}>编辑</Button>
            </div>
            <ModelServiceActions key={JSON.stringify([service.protocol, service.model, service.base_url,
              service.vendor, service.node, service.supports_vision, service.supports_audio, service.supports_video, service.verification?.at])}
              service={service} onChanged={reading.reload}
              shared={Boolean(reading.data?.services.some(other => other.id !== service.id && other.protocol === service.protocol))} />
          </article>;
        })}
      </div>
      {editing ? (
        <ModelServiceForm
          key={editing.service === "new" ? "new" : editing.service.id}
          service={editing.service === "new" ? null : editing.service}
          formRef={serviceForm}
          protocols={reading.data?.protocols ?? []}
          vendors={reading.data?.model_vendors ?? []}
          nodes={nodes}
          onClose={() => { cancelledFrom.current = editOrigin.current; setEditing(null); }}
          onSaved={paused => {
            setEditing(null);
            reading.reload();
            if (paused) command.setNotice("服务已暂停，需重新启用。");
            servicesHeading.current?.focus();
          }}
        />
      ) : null}
    </section> : null}
    {reading.data && view === "services" ? <SupplyNodes nodes={nodes} onChanged={reading.reload} /> : null}
    {reading.data && view === "credits" ? <>
    {!accountId ? <PlatformSettings scope="credits" compact /> : null}
    <section className="platform-supply-section">
      {!accountId ? <h2>账号额度</h2> : null}
      <p>该账号所有 Agent 共用这份额度。</p>
      <div className="platform-account-list">
        {reading.data?.accounts.filter(row => accountId ? row.id === accountId : row.role !== "admin" || Boolean(row.agent_count)).map(row => (
          <article key={row.id}>
            <div className="platform-account-summary">
              <div>
                <h3>{accountId ? row.name : <a href={`/admin/#/accounts?account=${encodeURIComponent(row.id)}&tab=credits`}>{row.name}</a>}</h3>
                <p>可用 {accountAmount(row.remaining_cny)} · 已使用 {accountAmount(row.charged_cny)} · 预留 {accountAmount(row.reserved_cny)}</p>
              </div>
              <Button variant="secondary"

                onClick={() => {
                  setAccount(row.id);
                  setAllowance(""); setOperation("add");
                  setReason("内测体验额度"); adjustmentId.current = crypto.randomUUID();
                }}
              >
                调整额度
              </Button>
            </div>
          </article>
        ))}
      </div>
      {account ? (
        <form className="platform-admin-form" onSubmit={saveAllowance}>
          <h3>调整 {reading.data?.accounts.find(row => row.id === account)?.name} 的额度</h3>
          <Field className="field" label="调整方式"><Select label="调整方式" value={operation} disabled={command.pending} onValueChange={value => { setOperation(value); adjustmentId.current = crypto.randomUUID(); }}
            items={[{ value: "add", label: "增加额度" }, { value: "remove", label: "收回可用额度" }]} /></Field>
          <Field className="field" label={operation === "add" ? "增加金额（元）" : "收回金额（元）"}><NumberField
              label={operation === "add" ? "增加金额（元）" : "收回金额（元）"}
              min="0.01"
              step="0.01"
              required
              value={allowance}
              disabled={command.pending}
              onChange={event => { setAllowance(event.target.value); adjustmentId.current = crypto.randomUUID(); }}
            /></Field>
          <Field className="field" label="用途"><input
              className="text-input"
              required
              maxLength={500}
              value={reason}
              onChange={event => setReason(event.target.value)}
            /></Field>
          <div className="platform-actions">
            <Button variant="primary" type="submit" disabled={command.pending || !reason.trim() || !(Number(allowance) > 0)}>{operation === "add" ? "确认增加" : "确认收回"}</Button>
            <Button variant="ghost" type="button" onClick={() => setAccount("")}>取消</Button>
          </div>
        </form>
      ) : null}
    </section></> : null}
    {reading.data && view === "calls" ? <section className="platform-supply-section">
      {!accountId ? <h2>最近调用</h2> : null}
      {!reading.data?.calls.length ? (
        <p>还没有通过平台服务发生的调用。</p>
      ) : (
        <ol className="platform-audit-list">
          {reading.data.calls.filter(call => !accountId || call.account_id === accountId).map(call => (
            <li key={call.id}>
              <div className="platform-audit-top">
                <strong>
                  {accountId ? call.agent_name || "创作助手" : <a href={`/admin/#/accounts?account=${encodeURIComponent(call.account_id)}&tab=calls`}>{accountName(call.account_id) || call.account_id}</a>}
                </strong>
                {!accountId && call.agent_name ? <span>{call.agent_name}</span> : null}
                <span>
                  {serviceTitle(call.service_id) || call.service_id}
                </span>
                <time>{new Date(call.created_at * 1000).toLocaleString("zh-CN")}</time>
              </div>
              <p>
                {call.status === "completed"
                  ? `已扣额度 ${accountAmount(call.charged_cny ?? 0)}`
                  : call.status === "rejected"
                    ? "未完成服务，未计费"
                    : `${call.status === "unknown" ? "结果待核对" : "进行中"} · 预留 ${accountAmount(call.reserved_cny)}`}
              </p>
              {Number.isInteger(call.usage?.characters) && (call.usage?.characters ?? -1) >= 0 ? (
                <p className="platform-meta">{`供应商计费字符：${call.usage!.characters}${call.usage?.supplier_actual_cost === null ? " · 供应商实际费用未提供" : ""}`}</p>
              ) : null}
              {call.usage?.supplier_message ? (
                <p className="platform-call-said">{`供应商：${call.usage.supplier_message}`}</p>
              ) : null}
              {call.status === "unknown" ? (
                <Button variant="secondary"

                  onClick={() => {
                    setReview(call);
                    setActual("");
                    setEvidence("");
                  }}
                >
                  核对费用
                </Button>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      {review ? (
        <form className="platform-admin-form" onSubmit={reconcile}>
          <h3>核对 {[accountId ? "" : accountName(review.account_id), review.agent_name, serviceTitle(review.service_id)]
            .filter(Boolean).join(" · ") || "调用"}</h3>
          <p>请先核对供应商记录。没有费用依据时保留预留；这里不会重发请求。</p>
          <Field className="field" label="确认的平台费用（元）"><NumberField
              label="确认的平台费用（元）"
              min="0"
              step="0.000001"
              value={actual}
              required
              onChange={event => setActual(event.target.value)}
            /></Field>
          <Field className="field" label="核对依据"><input
              className="text-input"
              value={evidence}
              required
              maxLength={500}
              onChange={event => setEvidence(event.target.value)}
            /></Field>
          <div className="platform-actions">
            <Button variant="primary" type="submit" disabled={command.pending || !evidence.trim()}>确认结算</Button>
            <Button variant="ghost" type="button"  onClick={() => setReview(null)}>取消</Button>
          </div>
        </form>
      ) : null}
    </section> : null}
  </>;
}
